import useSWR, { unstable_serialize } from 'swr';
import { RequestParams } from 'integration/generated/transactions/http-client';
import { transactionsPrice } from 'integration/generated/transactions';
import { getContractIdParamFromContext, getSettings, useSettings } from '@/data/Settings';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { GetServerSidePropsContext } from 'next';
import { Cache } from '@/data/types/Cache';
import { getUser } from '@/data/User';
import { getServerSideCommon } from '@/data/utils/getServerSideCommon';
import { constructRequestParamsWithPreviewToken } from '@/data/utils/constructRequestParams';
import { getServerCacheScope } from '@/data/cache/getServerCacheScope';
import { PricePrice } from 'integration/generated/transactions/data-contracts';
import { shrink } from '@/data/utils/keyUtil';

type PriceByQuery = {
	q?: 'byPartNumbers' | 'byCatalogEntryIds';
	partNumber?: string[] | string;
	catalogEntryId?: string[] | string;
	contractId?: string;
	currency?: 'MXN';
	quantity?: string;
	uom?: string;
	date?: string;
	checkEntitlement?: 'true' | 'false';
	dynamicKitAsItem?: 'true' | 'false';
	profileName?: 'IBM_Store_EntitledPrice_All' | 'IBM_Store_EntitledPrice_RangePrice_All';
};

export const priceByFetcher =
	(pub: boolean) => async (storeId: string, query: PriceByQuery, params: RequestParams) => {
		const response = await transactionsPrice(pub).priceBy(storeId, query, params);
		return response;
	};

const DATA_KEY_PRICE_BY = 'PriceBy';

const defaultQuery: PriceByQuery = {
	q: 'byCatalogEntryIds',
	checkEntitlement: 'false',
	profileName: 'IBM_Store_EntitledPrice_RangePrice_All',
	currency: 'MXN',
};

export const getPriceBy = async (
	cache: Cache,
	query: PriceByQuery,
	context: GetServerSidePropsContext
) => {
	const settings = await getSettings(cache, context);
	const user = await getUser(cache, context);
	const { storeId, langId } = getServerSideCommon(settings, context);

	const _query = {
		storeId,
		langId,
		...{ ...defaultQuery, ...query },
		...getContractIdParamFromContext(user?.context),
	};

	const key = unstable_serialize([shrink(_query), DATA_KEY_PRICE_BY]);
	const params = constructRequestParamsWithPreviewToken({ context });
	const cacheScope = getServerCacheScope(context, user.context);

	const value =
		(cache.get(key, cacheScope) as Promise<PricePrice | undefined>) ||
		priceByFetcher(false)(storeId, query, params);

	cache.set(key, value, cacheScope);

	return (await value) ?? undefined;
};

export const usePriceBy = (args?: PriceByQuery) => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();

	const rawSwr = useSWR(
		storeId && (args?.catalogEntryId || args?.partNumber)
			? [{ storeId, ...{ ...defaultQuery, ...args } }, DATA_KEY_PRICE_BY]
			: null,
		async ([{ storeId, ...query }]) => priceByFetcher(true)(storeId, query, params)
	);

	return rawSwr;
};
